import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Send, Users, AlertCircle, CheckCircle, Zap, CircleCheck, CircleX } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_LABELS = {
  due_reminder: 'Due Date Reminder',
  missed_payment: 'Missed Payment Alert',
  payment_confirmation: 'Payment Confirmation',
  noc_ready: 'NOC Ready',
  booking: 'Booking Confirmation',
  approval: 'Application Approved',
};

const AUDIENCES = [
  { key: 'all', label: 'All Customers', icon: Users },
  { key: 'defaulters', label: 'Defaulters Only', icon: AlertCircle },
  { key: 'due_soon', label: 'Due Soon', icon: CheckCircle },
];

export default function WhatsAppReminders() {
  const [template, setTemplate] = useState('due_reminder');
  const [audience, setAudience] = useState('all');
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoSending, setAutoSending] = useState(false);
  const [preview, setPreview] = useState('');
  const [twilioConfigured, setTwilioConfigured] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/whatsapp/status`, { withCredentials: true })
      .then(({ data }) => setTwilioConfigured(!!data.twilio_configured))
      .catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const params = (audience && audience !== 'all') ? `?category=${audience}` : '';
      const { data } = await axios.get(`${API_URL}/api/whatsapp/broadcast/${template}${params}`, { withCredentials: true });
      setRecipients(data.recipients || []);
      setPreview(data.recipients?.[0]?.message || '');
      toast.success(`Generated ${data.count} messages`);
    } catch { toast.error('Failed to generate'); }
    finally { setLoading(false); }
  };

  const autoSend = async () => {
    setAutoSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/whatsapp/auto-send?template=${template}&category=${audience === 'all' ? 'due_soon' : audience}`,
        {}, { withCredentials: true }
      );
      if (data.twilio_configured) {
        toast.success(`Auto-sent to ${data.sent} customers via Twilio (${data.pending} pending)`);
      } else {
        toast(`Twilio not configured — ${data.total} messages ready for manual send below`, { duration: 5000 });
        // Show the wa.me links as recipient list
        setRecipients((data.results || []).map(r => ({
          name: r.customer_name || '',
          mobile: r.mobile || '',
          wa_link: r.wa_link || '',
          message: '',
        })));
      }
    } catch { toast.error('Auto-send failed'); }
    finally { setAutoSending(false); }
  };

  const sendToOne = (link) => window.open(link, '_blank');
  const sendAll = () => {
    recipients.forEach((r, i) => setTimeout(() => window.open(r.wa_link, '_blank'), i * 300));
    toast.success(`Opening ${recipients.length} WhatsApp chats`);
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-reminders">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="whatsapp-title">WhatsApp Reminders</h1>
          <p className="text-slate-400">Automated Twilio delivery + manual click-to-send fallback</p>
        </div>
        <div className={`flex items-center space-x-2 px-3 py-2 rounded-sm border text-sm ${twilioConfigured ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-slate-800 border-slate-700 text-slate-400'}`}>
          {twilioConfigured ? <><CircleCheck className="h-4 w-4" /> <span>Twilio Connected</span></> : <><CircleX className="h-4 w-4" /> <span>Twilio Not Configured (manual send)</span></>}
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center space-x-2 text-slate-300">
            <MessageCircle className="h-5 w-5 text-emerald-500" />
            <h2 className="text-lg font-semibold text-white">Message Builder</h2>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Message Template</Label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="template-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  {Object.entries(TEMPLATE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="audience-select"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  {AUDIENCES.map(a => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex flex-col justify-end">
              <Button onClick={generate} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="generate-broadcast">
                {loading ? 'Generating...' : 'Preview Messages'}
              </Button>
            </div>
            <div className="space-y-2 flex flex-col justify-end">
              <Button onClick={autoSend} disabled={autoSending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="auto-send-button">
                <Zap className="h-4 w-4 mr-2" />
                {autoSending ? 'Sending...' : twilioConfigured ? 'Auto-Send Now' : 'Auto-Queue'}
              </Button>
            </div>
          </div>

          {preview && (
            <div className="space-y-2">
              <Label className="text-slate-300">Message Preview</Label>
              <Textarea readOnly value={preview}
                className="bg-slate-800 border-slate-700 text-white font-mono text-xs whitespace-pre-wrap"
                rows={7} data-testid="message-preview" />
            </div>
          )}

          {!twilioConfigured && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-sm p-3 text-sm text-amber-400">
              <strong>Enable automation:</strong> add <code className="bg-slate-900 px-1 rounded">TWILIO_ACCOUNT_SID</code>,
              <code className="bg-slate-900 px-1 rounded ml-1">TWILIO_AUTH_TOKEN</code>, and
              <code className="bg-slate-900 px-1 rounded ml-1">TWILIO_WHATSAPP_NUMBER</code> to backend/.env — reminders will start firing without any operator clicks.
            </div>
          )}
        </CardContent>
      </Card>

      {recipients.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-emerald-500" />
                <h2 className="text-lg font-semibold text-white">{recipients.length} Recipients</h2>
              </div>
              <Button onClick={sendAll} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-all-button">
                <Send className="h-4 w-4 mr-2" /> Send to All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {recipients.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-800/40 border border-slate-700 rounded-sm" data-testid="recipient-row">
                  <div>
                    <div className="text-white font-medium">{r.name}</div>
                    <div className="text-xs text-slate-400 font-mono">{r.mobile}</div>
                  </div>
                  <Button size="sm" onClick={() => sendToOne(r.wa_link)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`send-one-${i}`}>
                    <Send className="h-3 w-3 mr-1" /> Send
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

